// ============================================================================
// Profile API - Centralized profile data access
// ============================================================================

import { supabase, auth } from 'app/auth/auth.js';

// ============================================================================
// Avatar Storage Configuration
// ============================================================================

const AVATAR_BUCKET = 'avatars';
const AVATAR_MAX_SIZE = 2 * 1024 * 1024; // 2MB
const AVATAR_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// ============================================================================
// Current User Profile
// ============================================================================

/**
 * Fetch the profile for the currently authenticated user.
 * Falls back to a sensible default profile when no data is returned or when Supabase errors occur.
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function getCurrentUserProfile() {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      return { data: null, error: userError };
    }

    // Try fetch without .single() to avoid errors when row missing
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('[profileApi] Failed to get user profile:', error);
      return { data: buildFallbackProfile(user), error: null };
    }

    if (!profiles || profiles.length === 0) {
      return { data: buildFallbackProfile(user), error: null };
    }

    return { data: profiles[0], error: null };

  } catch (error) {
    console.error('[profileApi] Unexpected error in getCurrentUserProfile:', error);
    const { data: { user } } = await supabase.auth.getUser();
    return { data: buildFallbackProfile(user), error: null };
  }
}

/**
 * Build a fallback profile when no profile exists in the database
 * @param {Object} user - Supabase user object
 * @returns {Object} Fallback profile
 */
function buildFallbackProfile(user) {
  const name = user?.email?.split('@')[0] || 'User';
  return {
    user_id: user?.id,
    email: user?.email,
    role: 'employee',
    full_name: name,
    short_name: name,
    hourly_rate: 0,
    timesheet_required: true,
    timesheet_start_date: new Date().toISOString().split('T')[0]
  };
}

// ============================================================================
// Profile Queries
// ============================================================================

/**
 * Fetch all profiles
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export async function getAllProfiles() {
  try {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('user_id, full_name, short_name, email, role, hourly_rate')
      .order('full_name', { ascending: true });

    if (error) {
      console.error('[profileApi] Failed to get all profiles:', error);
      return { data: [], error };
    }

    return { data: profiles || [], error: null };
  } catch (error) {
    console.error('[profileApi] Unexpected error in getAllProfiles:', error);
    return { data: [], error };
  }
}

/**
 * Get all available assignees (users) for task assignment.
 * Inactive (former) users are excluded - they should never be assignable.
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export async function getAvailableAssignees() {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      return { data: [], error: sessionError || new Error('No session') };
    }

    // NULL-safe exclusion: .neq alone would also drop NULL-role rows
    // (SQL three-valued logic), so allow NULL explicitly.
    // email + leave/overtime approval flags ride along so the dropdown-store
    // cache can serve the leave and overtime approver pickers without their
    // own per-open queries.
    const { data, error } = await supabase
      .from('profiles')
      .select('user_id, full_name, short_name, email, avatar_url, can_sign_off_budgets, can_review_budgets, can_approve_leave, can_manage_leave')
      .or('role.is.null,role.neq.inactive')
      .order('full_name');

    if (error) {
      // Return empty array for permission errors (graceful degradation)
      if (error.code === '42501') {
        return { data: [], error: null };
      }
      throw error;
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('[profileApi] Failed to get assignees:', error);
    return { data: [], error };
  }
}

/**
 * Get profiles by user IDs
 * @param {Array<string>} userIds - Array of user IDs
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getProfiles(userIds) {
  try {
    if (!userIds || userIds.length === 0) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('user_id, full_name, short_name')
      .in('user_id', userIds);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('[profileApi] Failed to get profiles:', error);
    return { data: null, error };
  }
}

/**
 * Search profiles by name
 * @param {string} query - Search query
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function searchProfiles(query = '') {
  try {
    const base = supabase.from('profiles');
    const sel = 'user_id, full_name, short_name';

    const { data, error } = query && query.length >= 1
      ? await base.select(sel).or(`full_name.ilike.%${query}%,short_name.ilike.%${query}%`).limit(20)
      : await base.select(sel).limit(20);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('[profileApi] Failed to search profiles:', error);
    return { data: null, error };
  }
}

// ============================================================================
// Avatar Management
// ============================================================================

/**
 * Remove all possible avatar files for a user (all supported extensions)
 * @param {string} userId
 */
async function removeAvatarFiles(userId) {
  const extensions = ['jpg', 'png', 'gif', 'webp'];
  await supabase.storage
    .from(AVATAR_BUCKET)
    .remove(extensions.map(ext => `${userId}/avatar.${ext}`));
}

/**
 * Upload and update the current user's avatar
 * @param {File} file - The image file to upload
 * @returns {Promise<{success: boolean, avatarUrl?: string, error?: string}>}
 */
export async function uploadAvatar(file) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Validate file type
    if (!AVATAR_ALLOWED_TYPES.includes(file.type)) {
      return { success: false, error: 'Invalid file type. Please use JPEG, PNG, GIF, or WebP.' };
    }

    // Validate file size
    if (file.size > AVATAR_MAX_SIZE) {
      return { success: false, error: 'File too large. Maximum size is 2MB.' };
    }

    // Generate unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/avatar.${fileExt}`;

    // Delete old avatar if exists
    await removeAvatarFiles(user.id);

    // Upload new avatar
    const { error: uploadError } = await supabase.storage
      .from(AVATAR_BUCKET)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      console.error('[profileApi] Avatar upload failed:', uploadError);
      return { success: false, error: 'Failed to upload image' };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(AVATAR_BUCKET)
      .getPublicUrl(fileName);

    const avatarUrl = urlData.publicUrl + '?t=' + Date.now(); // Cache busting

    // Update profile with new avatar URL
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: avatarUrl, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);

    if (updateError) {
      console.error('[profileApi] Failed to update avatar_url:', updateError);
      return { success: false, error: 'Failed to save avatar' };
    }

    // Update cached profile
    if (auth.profile) {
      auth.profile.avatar_url = avatarUrl;
    }

    return { success: true, avatarUrl };

  } catch (error) {
    console.error('[profileApi] Unexpected error uploading avatar:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Remove the current user's avatar
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function removeAvatar() {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Remove all possible avatar files
    await removeAvatarFiles(user.id);

    // Clear avatar URL in profile
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: null, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);

    if (updateError) {
      console.error('[profileApi] Failed to clear avatar_url:', updateError);
      return { success: false, error: 'Failed to remove avatar' };
    }

    // Update cached profile
    if (auth.profile) {
      auth.profile.avatar_url = null;
    }

    return { success: true };

  } catch (error) {
    console.error('[profileApi] Unexpected error removing avatar:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

// ============================================================================
// User Preferences
// ============================================================================

/**
 * Get the current user's list page size preference
 * Uses cached auth.profile for instant access (no DB query)
 * @returns {number} Page size (defaults to 500)
 */
export function getUserPageSize() {
  // Use cached profile from auth module (loaded on login)
  return auth.profile?.list_page_size || 200;
}

/**
 * Update the current user's list page size preference
 * @param {number} pageSize - New page size (25-1000)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function updateUserPageSize(pageSize) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Validate range
    if (pageSize < 25 || pageSize > 1000) {
      return { success: false, error: 'Page size must be between 25 and 1000' };
    }

    const { error } = await supabase
      .from('profiles')
      .update({ list_page_size: pageSize, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);

    if (error) {
      console.error('[profileApi] Failed to update page size:', error);
      return { success: false, error: 'Failed to save preference' };
    }

    // Update cached profile
    if (auth.profile) {
      auth.profile.list_page_size = pageSize;
    }

    return { success: true };
  } catch (error) {
    console.error('[profileApi] Unexpected error updating page size:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

// ============================================================================
// Text Expansions - Per-user shortcode → full text mappings
// ============================================================================

/**
 * Get the current user's text expansions (cached, instant)
 * @returns {Object} Key-value map of shortcode → expansion text
 */
export function getTextExpansions() {
    return auth.profile?.text_expansions || {};
}

/**
 * Update the current user's text expansions and refresh the in-memory Map
 * @param {Object} expansions - Full key-value map { "aa": "Annual Audit", ... }
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function updateTextExpansions(expansions) {
    try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
            return { success: false, error: 'Not authenticated' };
        }

        const { error } = await supabase
            .from('profiles')
            .update({ text_expansions: expansions, updated_at: new Date().toISOString() })
            .eq('user_id', user.id);

        if (error) {
            console.error('[profileApi] Failed to update text expansions:', error);
            return { success: false, error: 'Failed to save text expansions' };
        }

        // Update cached profile
        if (auth.profile) {
            auth.profile.text_expansions = expansions;
        }

        // Refresh the in-memory expansion Map
        const { loadExpansions } = await import('shared/utils/text-expansion.js');
        loadExpansions();

        return { success: true };
    } catch (error) {
        console.error('[profileApi] Unexpected error updating text expansions:', error);
        return { success: false, error: 'An unexpected error occurred' };
    }
}

// ============================================================================
// Sidebar Config - Per-user category management
// ============================================================================

const DEFAULT_SIDEBAR_CONFIG = { categories: [], assignments: {}, listOrders: {} };
const MAX_SIDEBAR_CATEGORIES = 10;

/**
 * Get current user's sidebar config (categories + assignments)
 * @returns {{ categories: Array, assignments: Object }}
 */
export function getSidebarConfig() {
  const config = auth.profile?.sidebar_config || { ...DEFAULT_SIDEBAR_CONFIG };
  // Ensure listOrders exists (backward compat for old profiles)
  if (!config.listOrders) config.listOrders = {};
  return config;
}

/**
 * Update the full sidebar config in the database and cache
 * @param {{ categories: Array, assignments: Object }} config
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function updateSidebarConfig(config) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { error } = await supabase
      .from('profiles')
      .update({ sidebar_config: config, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);

    if (error) {
      console.error('[profileApi] Failed to update sidebar config:', error);
      return { success: false, error: 'Failed to save sidebar config' };
    }

    if (auth.profile) {
      auth.profile.sidebar_config = config;
    }

    return { success: true };
  } catch (error) {
    console.error('[profileApi] Unexpected error updating sidebar config:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Add a new sidebar category
 * @param {string} name - Category name
 * @param {string} [icon] - Optional icon value (FA class or aer-* value)
 * @returns {Promise<{success: boolean, error?: string, categoryId?: string}>}
 */
export async function addSidebarCategory(name, icon) {
  const config = getSidebarConfig();

  if (config.categories.length >= MAX_SIDEBAR_CATEGORIES) {
    return { success: false, error: `Maximum ${MAX_SIDEBAR_CATEGORIES} categories allowed` };
  }

  const trimmedName = name.trim();
  if (!trimmedName) {
    return { success: false, error: 'Category name is required' };
  }

  const categoryId = crypto.randomUUID();
  const category = {
    id: categoryId,
    name: trimmedName,
    order: config.categories.length
  };
  if (icon) category.icon = icon;
  config.categories.push(category);

  const result = await updateSidebarConfig(config);
  return { ...result, categoryId: result.success ? categoryId : undefined };
}

/**
 * Update an existing sidebar category's name and/or icon.
 * Pass `icon` to change the group icon alongside the name; omit it to leave
 * the existing icon untouched.
 * @param {string} categoryId
 * @param {string} newName
 * @param {string} [icon] - Optional icon value (FA class or aer-* value)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function renameSidebarCategory(categoryId, newName, icon) {
  const config = getSidebarConfig();
  const category = config.categories.find(c => c.id === categoryId);

  if (!category) {
    return { success: false, error: 'Category not found' };
  }

  const trimmedName = newName.trim();
  if (!trimmedName) {
    return { success: false, error: 'Category name is required' };
  }

  category.name = trimmedName;
  if (icon !== undefined) category.icon = icon;
  return updateSidebarConfig(config);
}

/**
 * Delete a sidebar category - items become uncategorized (move to Pinned)
 * @param {string} categoryId
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteSidebarCategory(categoryId) {
  const config = getSidebarConfig();
  config.categories = config.categories.filter(c => c.id !== categoryId);

  // Remove all assignments pointing to this category
  for (const [listId, catId] of Object.entries(config.assignments)) {
    if (catId === categoryId) {
      delete config.assignments[listId];
    }
  }

  // Recompute order
  config.categories.forEach((cat, i) => { cat.order = i; });

  return updateSidebarConfig(config);
}

/**
 * Reorder sidebar categories
 * @param {Array<string>} orderedIds - Category IDs in desired order
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function reorderSidebarCategories(orderedIds) {
  const config = getSidebarConfig();
  const catMap = new Map(config.categories.map(c => [c.id, c]));

  config.categories = orderedIds
    .map(id => catMap.get(id))
    .filter(Boolean)
    .map((cat, i) => ({ ...cat, order: i }));

  return updateSidebarConfig(config);
}

/**
 * Reorder lists within a sidebar category (or the uncategorized "pinned" group)
 * @param {Array<string>} orderedListIds - List IDs in desired order
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function reorderListsInCategory(orderedListIds) {
  const config = getSidebarConfig();
  if (!config.listOrders) config.listOrders = {};

  // Assign sequential order numbers to the provided list IDs
  orderedListIds.forEach((listId, i) => {
    config.listOrders[listId] = i;
  });

  return updateSidebarConfig(config);
}

/**
 * Assign a list to a sidebar category (or unassign)
 * @param {string} listId - The list/programmed ID
 * @param {string|null} categoryId - Category ID, or null to unassign
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function assignListToCategory(listId, categoryId) {
  const config = getSidebarConfig();

  if (categoryId) {
    const exists = config.categories.some(c => c.id === categoryId);
    if (!exists) {
      return { success: false, error: 'Category not found' };
    }
    config.assignments[listId] = categoryId;
  } else {
    delete config.assignments[listId];
  }

  return updateSidebarConfig(config);
}

/**
 * Apply a full sidebar layout in one shot: replace the category structure AND
 * the pinned list set together (e.g. resetting to the firm default preset).
 * Existing non-layout config keys (hiddenResources, hiddenTeamMembers, ...) are
 * preserved - only categories / assignments / listOrders are overwritten.
 * @param {{ categories: Array, assignments: Object, listOrders: Object, pinnedIds: Array<string> }} layout
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function applySidebarLayout({ categories, assignments, listOrders, pinnedIds }) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      return { success: false, error: 'Not authenticated' };
    }

    const newConfig = {
      ...getSidebarConfig(),
      categories: categories || [],
      assignments: assignments || {},
      listOrders: listOrders || {}
    };
    const newPinned = pinnedIds || [];

    const { error } = await supabase
      .from('profiles')
      .update({
        sidebar_config: newConfig,
        pinned_task_lists: newPinned,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', user.id);

    if (error) {
      console.error('[profileApi] Failed to apply sidebar layout:', error);
      return { success: false, error: 'Failed to apply sidebar layout' };
    }

    if (auth.profile) {
      auth.profile.sidebar_config = newConfig;
      auth.profile.pinned_task_lists = newPinned;
    }

    return { success: true };
  } catch (error) {
    console.error('[profileApi] Unexpected error applying sidebar layout:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}
